import React, { useState, useEffect, useRef } from 'react';
import { tradingAPI } from '../../../../services/tradingAPI';
import '../../../../css/trading.css';

const Watchlist = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchWatchlist();
  }, []);

  const fetchWatchlist = async () => {
    try {
      setLoading(true);
      const response = await tradingAPI.getWatchlistPrices();
      setItems(response.data || []);
    } catch (err) {
      // Fall back to basic list without prices
      try {
        const response = await tradingAPI.getWatchlist();
        setItems(response.data || []);
      } catch {
        setItems([]);
      }
    } finally {
      setLoading(false);
    }
  };

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const response = await tradingAPI.searchStocks(query);
        setSearchResults(response.data.results || []);
        setShowDropdown(true);
      } catch {
        setSearchResults([]);
      }
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const addTicker = async (symbol, name) => {
    // Check if already watching
    if (items.some(i => i.symbol === symbol)) return;

    setAdding(true);
    try {
      await tradingAPI.addToWatchlist({ symbol, name });
      setQuery('');
      setShowDropdown(false);
      fetchWatchlist();
    } catch (err) {
      console.error('Failed to add:', err);
    } finally {
      setAdding(false);
    }
  };

  const handleDirectAdd = (e) => {
    e.preventDefault();
    const trimmed = query.trim().toUpperCase();
    if (trimmed && trimmed.length <= 5 && !trimmed.includes(' ')) {
      addTicker(trimmed, '');
    }
  };

  const removeTicker = async (id) => {
    try {
      await tradingAPI.removeFromWatchlist(id);
      setItems(items.filter(i => i.id !== id));
    } catch (err) {
      console.error('Failed to remove:', err);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '--';
    return `$${parseFloat(value).toFixed(2)}`;
  };

  if (loading) {
    return <div className="loading">Loading watchlist...</div>;
  }

  return (
    <div className="watchlist-tab">
      <div className="section-header">
        <h2>Watchlist</h2>
      </div>
      <p style={{ color: 'rgba(255,255,255,0.5)', marginBottom: '1.5rem' }}>
        Follow tickers without investing. Search by ticker or company name.
      </p>

      {/* Add ticker */}
      <form onSubmit={handleDirectAdd} className="search-form" style={{ marginBottom: '2rem' }}>
        <div className="search-input-wrapper" ref={dropdownRef}>
          <div className="search-input-group">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Add ticker or search company name..."
              className="search-input"
            />
            <button type="submit" className="btn btn-primary" disabled={adding}>
              {adding ? 'Adding...' : 'Add'}
            </button>
          </div>

          {showDropdown && searchResults.length > 0 && (
            <div className="search-dropdown">
              {searchResults.map((result, idx) => (
                <div
                  key={idx}
                  className="search-dropdown-item"
                  onClick={() => addTicker(result.symbol, result.longname || result.shortname)}
                >
                  <span className="dropdown-symbol">{result.symbol}</span>
                  <span className="dropdown-name">{result.longname || result.shortname}</span>
                  <span className="dropdown-exchange">{result.exchange}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </form>

      {/* Watchlist table */}
      {items.length > 0 ? (
        <div className="positions-table">
          <table>
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Name</th>
                <th>Price</th>
                <th>Change</th>
                <th>Change %</th>
                <th>Added</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td className="symbol">{item.symbol}</td>
                  <td style={{ color: 'rgba(255,255,255,0.7)' }}>{item.name || '--'}</td>
                  <td>{formatCurrency(item.price)}</td>
                  <td className={item.change >= 0 ? 'positive' : 'negative'}>
                    {item.change != null ? `${item.change >= 0 ? '+' : ''}${item.change.toFixed(2)}` : '--'}
                  </td>
                  <td className={item.change_pct >= 0 ? 'positive' : 'negative'}>
                    {item.change_pct != null ? `${item.change_pct >= 0 ? '+' : ''}${item.change_pct.toFixed(2)}%` : '--'}
                  </td>
                  <td style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem' }}>
                    {new Date(item.added_at).toLocaleDateString()}
                  </td>
                  <td>
                    <button
                      className="btn btn-secondary"
                      style={{ padding: '0.3rem 0.6rem', fontSize: '0.75rem' }}
                      onClick={() => removeTicker(item.id)}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state">
          <h3>No Tickers on Your Watchlist</h3>
          <p>Search above to add tickers you want to follow without investing.</p>
        </div>
      )}
    </div>
  );
};

export default Watchlist;
